import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { Alert } from '../../../forms/components/Alert/Alert';
import { AlertLevel } from '../../../forms/components/Alert/props';
import { FormLabel } from '../../../forms/components/FormLabel/FormLabel';
import { TrackSearchField } from '../../../music24/components/SearchField/TrackSearchField';
import { MeiliTrackData } from '../../../music24/components/SearchField/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { RecentCatalogItems } from '../common/RecentCatalogItems';
import { CatalogTrackResolver } from './CatalogTrackResolver';
import { CatalogTrackSearchStepProps } from './props';

/** Catalog search needs the web search field, so it is off on React Native. */
export const isCatalogTrackSearchSupported: boolean = true;

const CatalogTrackSearchStepComponent: React.FC<CatalogTrackSearchStepProps> = ({
  accountId,
  initialTrackMaid,
  onResults,
  onUseUrlInstead,
}) => {
  const intl = useIntl();
  const [trackMaid, setTrackMaid] = useState<string | undefined>(initialTrackMaid);

  const onTrackSelected = useCallback((track: MeiliTrackData) => {
    setTrackMaid(track.id);
  }, []);

  const onCatalogItemPicked = useCallback((item: CatalogItem) => {
    // Only claimed rows are offered, and `RecentCatalogItems` drops the ones
    // with no maid, so this is always set.
    setTrackMaid(item.catalogTrackMaid ?? undefined);
  }, []);

  if (trackMaid !== undefined) {
    return <CatalogTrackResolver trackMaid={trackMaid} onResults={onResults} onUseUrlInstead={onUseUrlInstead} />;
  }

  return (
    <>
      <RecentCatalogItems accountId={accountId} kind={CatalogItemKind.claimed} onPick={onCatalogItemPicked} />
      <FormLabel>
        {intl.formatMessage({
          id: 'app.smart_links.release_smart_link.catalog.label',
          defaultMessage: 'Find your release',
        })}
      </FormLabel>
      <div className="mb-3">
        <TrackSearchField
          autoFocus
          placeholder={intl.formatMessage({
            id: 'app.smart_links.release_smart_link.catalog.placeholder',
            defaultMessage: 'Search by track or artist name',
          })}
          onTrackSelected={onTrackSelected}
        />
      </div>
      <Alert level={AlertLevel.info} center={false}>
        {intl.formatMessage({
          id: 'app.smart_links.release_smart_link.catalog.help',
          defaultMessage:
            'Pick your release from our catalog. We add every music service link we already know and search for the rest.',
        })}
      </Alert>
      <SMLButton color="link" onClick={onUseUrlInstead}>
        {intl.formatMessage({
          id: 'app.smart_links.release_smart_link.catalog.use_url',
          defaultMessage: 'Paste a link instead',
        })}
      </SMLButton>
    </>
  );
};

export const CatalogTrackSearchStep = TypedMemo(CatalogTrackSearchStepComponent);
